import jsPDF from 'jspdf';
import { APP_NAME, APP_SUBTITLE, CATEGORY_LABELS, CLIENT_TYPE_LABELS, MARKET_LABELS, PROFILE_LABELS } from '../data/knowledgeBase';
import type { PreparationResult } from '../types';

const COLORS = {
  blue900: [0, 47, 134] as [number, number, number],
  blue700: [0, 61, 166] as [number, number, number],
  blue500: [29, 88, 199] as [number, number, number],
  cyan: [89, 198, 207] as [number, number, number],
  surface: [240, 245, 255] as [number, number, number],
  line: [195, 211, 243] as [number, number, number],
  text: [16, 34, 65] as [number, number, number],
  textSoft: [74, 97, 141] as [number, number, number],
  white: [255, 255, 255] as [number, number, number]
};

const PAGE = {
  width: 210,
  height: 297,
  marginX: 14,
  top: 16,
  bottom: 16,
  headerHeight: 22,
  footerHeight: 20
};

const slugify = (value: string): string =>
  value
    .toLocaleLowerCase('pt-BR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const loadImageAsDataUrl = async (src: string): Promise<string> => {
  const response = await fetch(src);
  if (!response.ok) {
    throw new Error('Não foi possível carregar a identidade visual da C.Vale para exportar o PDF.');
  }

  const blob = await response.blob();

  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }

      reject(new Error('Não foi possível preparar o logo para o PDF.'));
    };
    reader.onerror = () => reject(new Error('Não foi possível ler o logo para o PDF.'));
    reader.readAsDataURL(blob);
  });
};

const pageContentBottom = (): number => PAGE.height - PAGE.bottom - PAGE.footerHeight;

const formatGeneratedAt = (isoString: string): string => {
  const date = new Date(isoString);
  const formatted = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);

  return formatted.replace(' às ', ', ');
};

const addPageChrome = (doc: jsPDF, logoDataUrl: string, footerLines: string[], pageNumber: number): void => {
  const headerX = PAGE.marginX;
  const headerY = PAGE.top;
  const headerWidth = PAGE.width - PAGE.marginX * 2;

  doc.setFillColor(...COLORS.blue900);
  doc.roundedRect(headerX, headerY, headerWidth, PAGE.headerHeight, 5, 5, 'F');
  doc.setFillColor(...COLORS.blue700);
  doc.roundedRect(headerX + headerWidth * 0.52, headerY, headerWidth * 0.48, PAGE.headerHeight, 5, 5, 'F');
  doc.setFillColor(44, 105, 206);
  doc.roundedRect(headerX + headerWidth * 0.72, headerY, headerWidth * 0.28, PAGE.headerHeight, 5, 5, 'F');
  doc.setFillColor(128, 210, 218);
  doc.roundedRect(headerX + headerWidth * 0.84, headerY, headerWidth * 0.16, PAGE.headerHeight, 5, 5, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13.5);
  doc.setTextColor(...COLORS.white);
  doc.text(APP_NAME, PAGE.marginX + 5, PAGE.top + 8);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.6);
  doc.text(APP_SUBTITLE, PAGE.marginX + 5, PAGE.top + 14);

  doc.addImage(logoDataUrl, 'PNG', PAGE.width - PAGE.marginX - 30, PAGE.top + 3.4, 24, 11.8, undefined, 'FAST');

  const footerY = PAGE.height - PAGE.bottom - 8.4;
  const footerWidth = PAGE.width - PAGE.marginX * 2;
  doc.setFillColor(...COLORS.blue900);
  doc.roundedRect(PAGE.marginX, footerY, footerWidth, 1.2, 0.6, 0.6, 'F');
  doc.setFillColor(...COLORS.blue500);
  doc.roundedRect(PAGE.marginX + footerWidth * 0.62, footerY, footerWidth * 0.38, 1.2, 0.6, 0.6, 'F');
  doc.setFillColor(157, 223, 229);
  doc.roundedRect(PAGE.marginX + footerWidth * 0.86, footerY, footerWidth * 0.14, 1.2, 0.6, 0.6, 'F');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.8);
  doc.setTextColor(...COLORS.textSoft);
  doc.text(footerLines[0], PAGE.marginX, footerY - 2.2);
  doc.text(footerLines.slice(1), PAGE.marginX, footerY + 5.6);
  doc.text(`Página ${pageNumber}`, PAGE.width - PAGE.marginX, footerY + 5.6, { align: 'right' });
};

const ensureSpace = (
  doc: jsPDF,
  cursorY: number,
  heightNeeded: number,
  logoDataUrl: string,
  footerLines: string[],
  pageNumberRef: { current: number }
): number => {
  if (cursorY + heightNeeded <= pageContentBottom()) {
    return cursorY;
  }

  doc.addPage();
  pageNumberRef.current += 1;
  addPageChrome(doc, logoDataUrl, footerLines, pageNumberRef.current);
  return PAGE.top + PAGE.headerHeight + 8;
};

const drawWrappedText = (
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight = 4.8
): number => {
  const lines = doc.splitTextToSize(text, maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
};

const estimateTextHeight = (doc: jsPDF, text: string, maxWidth: number, lineHeight = 4.8): number => {
  const lines = doc.splitTextToSize(text, maxWidth);
  return lines.length * lineHeight;
};

const drawMetaChip = (doc: jsPDF, label: string, value: string, x: number, y: number, width: number): void => {
  doc.setFillColor(...COLORS.surface);
  doc.setDrawColor(...COLORS.line);
  doc.roundedRect(x, y, width, 13, 3, 3, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.4);
  doc.setTextColor(...COLORS.blue700);
  doc.text(label.toUpperCase(), x + 3, y + 4.3);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.4);
  doc.setTextColor(...COLORS.text);
  const valueLines = doc.splitTextToSize(value, width - 6);
  doc.text(valueLines, x + 3, y + 9);
};

const drawPanel = (
  doc: jsPDF,
  title: string,
  subtitle: string,
  y: number,
  height: number,
  fill: [number, number, number] = COLORS.white
): void => {
  doc.setFillColor(...fill);
  doc.setDrawColor(...COLORS.line);
  doc.roundedRect(PAGE.marginX, y, PAGE.width - PAGE.marginX * 2, height, 5, 5, 'FD');

  doc.setFillColor(...COLORS.blue700);
  doc.roundedRect(PAGE.marginX + 3, y + 3, 52, 8, 2.6, 2.6, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.2);
  doc.setTextColor(...COLORS.white);
  doc.text(title, PAGE.marginX + 6, y + 8.2);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.8);
  doc.setTextColor(...COLORS.textSoft);
  doc.text(subtitle, PAGE.marginX + 58, y + 8.2);
};

const drawBulletList = (
  doc: jsPDF,
  items: string[],
  x: number,
  y: number,
  width: number,
  lineHeight = 4.6
): number => {
  let cursorY = y;

  items.forEach((item) => {
    const lines = doc.splitTextToSize(item, width - 5);
    doc.setTextColor(...COLORS.blue700);
    doc.text('•', x, cursorY);
    doc.setTextColor(...COLORS.text);
    doc.text(lines, x + 4, cursorY);
    cursorY += lines.length * lineHeight + 1.2;
  });

  return cursorY;
};

const drawSubBlock = (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  title: string,
  items: string[],
  accent: [number, number, number],
  fixedHeight?: number
): number => {
  const titleHeight = 8;
  const contentHeight = items.length
    ? items.reduce((acc, item) => acc + estimateTextHeight(doc, item, width - 11, 4.4) + 1.2, 0) + 7
    : 10;
  const totalHeight = fixedHeight ?? titleHeight + contentHeight + 4;

  doc.setFillColor(...COLORS.surface);
  doc.setDrawColor(...COLORS.line);
  doc.roundedRect(x, y, width, totalHeight, 4, 4, 'FD');

  doc.setFillColor(...accent);
  doc.roundedRect(x, y, width, titleHeight, 4, 4, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.2);
  doc.setTextColor(...COLORS.white);
  doc.text(title, x + 3, y + 5.4);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.7);
  const finalY = items.length
    ? drawBulletList(doc, items, x + 3, y + 13, width - 6, 4.1)
    : drawWrappedText(doc, 'Sem conteúdo disponível.', x + 3, y + 13, width - 6, 4.1);

  return Math.max(totalHeight, finalY - y + 4);
};

const drawStageCard = (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  index: number,
  title: string,
  summary: string,
  fixedHeight?: number
): number => {
  const summaryHeight = estimateTextHeight(doc, summary, width - 14, 4.3);
  const height = fixedHeight ?? Math.max(22, summaryHeight + 14);

  doc.setFillColor(...COLORS.white);
  doc.setDrawColor(...COLORS.line);
  doc.roundedRect(x, y, width, height, 4, 4, 'FD');

  doc.setFillColor(...COLORS.cyan);
  doc.circle(x + 8, y + 8, 4.3, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.2);
  doc.setTextColor(...COLORS.blue900);
  doc.text(String(index), x + 8, y + 9.2, { align: 'center' });

  doc.setTextColor(...COLORS.blue700);
  doc.text(title, x + 15, y + 7.2);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.8);
  doc.setTextColor(...COLORS.text);
  drawWrappedText(doc, summary, x + 15, y + 12.3, width - 18, 4.3);

  return height;
};

const drawContextPanel = (doc: jsPDF, result: PreparationResult, y: number): number => {
  const height = 42;
  drawPanel(doc, 'Panorama executivo', 'Contexto da reunião e direcionamento comercial', y, height, COLORS.surface);

  const chipWidth = (PAGE.width - PAGE.marginX * 2 - 12) / 4;
  drawMetaChip(doc, 'Perfil', PROFILE_LABELS[result.contexto.perfil], PAGE.marginX + 4, y + 14, chipWidth);
  drawMetaChip(doc, 'Categoria', CATEGORY_LABELS[result.contexto.categoria], PAGE.marginX + 6 + chipWidth, y + 14, chipWidth);
  drawMetaChip(doc, 'Mercado', MARKET_LABELS[result.contexto.mercado], PAGE.marginX + 8 + chipWidth * 2, y + 14, chipWidth);
  drawMetaChip(doc, 'Tipo de cliente', CLIENT_TYPE_LABELS[result.contexto.tipoCliente], PAGE.marginX + 10 + chipWidth * 3, y + 14, chipWidth);

  return y + height + 6;
};

const drawScenarioPanel = (doc: jsPDF, result: PreparationResult, y: number): number => {
  const scenarioHeight = Math.max(
    28,
    estimateTextHeight(doc, result.cenarioOriginal, PAGE.width - PAGE.marginX * 2 - 10, 4.5) + 16
  );
  drawPanel(doc, 'Cenário da reunião', result.leituraCenario.tipoCenario, y, scenarioHeight);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...COLORS.text);
  drawWrappedText(doc, result.cenarioOriginal, PAGE.marginX + 5, y + 17, PAGE.width - PAGE.marginX * 2 - 10, 4.5);

  return y + scenarioHeight + 6;
};

const drawBehaviorPanel = (doc: jsPDF, result: PreparationResult, y: number): number => {
  const colWidth = (PAGE.width - PAGE.marginX * 2 - 16) / 3;
  const heights = [
    result.abordagemPerfil.linguagem,
    [...result.abordagemPerfil.postura, ...result.abordagemPerfil.ritmo],
    result.abordagemPerfil.fechamento
  ].map((items) => Math.max(34, items.reduce((acc, item) => acc + estimateTextHeight(doc, item, colWidth - 11, 4.1) + 1.6, 0) + 24));
  const uniformHeight = Math.max(...heights);
  const totalHeight = uniformHeight + 22;
  drawPanel(doc, 'Orientação comportamental', 'Como conduzir a reunião conforme o perfil do cliente', y, totalHeight);
  drawSubBlock(doc, PAGE.marginX + 4, y + 14, colWidth, 'Linguagem recomendada', result.abordagemPerfil.linguagem, COLORS.blue700, uniformHeight);
  drawSubBlock(
    doc,
    PAGE.marginX + 8 + colWidth,
    y + 14,
    colWidth,
    'Postura e ritmo',
    [...result.abordagemPerfil.postura, ...result.abordagemPerfil.ritmo],
    COLORS.blue500,
    uniformHeight
  );
  drawSubBlock(
    doc,
    PAGE.marginX + 12 + colWidth * 2,
    y + 14,
    colWidth,
    'Fechamento ideal',
    result.abordagemPerfil.fechamento,
    COLORS.cyan,
    uniformHeight
  );
  return y + totalHeight + 6;
};

const drawTripleBlockPanel = (
  doc: jsPDF,
  title: string,
  subtitle: string,
  blocks: Array<{ title: string; items: string[]; accent: [number, number, number] }>,
  y: number,
  options?: {
    minHeight?: number;
    extraHeight?: number;
  }
): number => {
  const colWidth = (PAGE.width - PAGE.marginX * 2 - 16) / 3;
  const heights = blocks.map((block) =>
    Math.max(options?.minHeight ?? 36, block.items.reduce((acc, item) => acc + estimateTextHeight(doc, item, colWidth - 11, 4.1) + 1.8, 0) + 34)
  );

  const uniformHeight = Math.max(...heights);
  const totalHeight = uniformHeight + (options?.extraHeight ?? 24);
  drawPanel(doc, title, subtitle, y, totalHeight);
  blocks.forEach((block, index) => {
    drawSubBlock(
      doc,
      PAGE.marginX + 4 + index * (colWidth + 4),
      y + 14,
      colWidth,
      block.title,
      block.items,
      block.accent,
      uniformHeight
    );
  });
  return y + totalHeight + 6;
};

const drawSpinPanel = (doc: jsPDF, result: PreparationResult, y: number): number => {
  const halfWidth = (PAGE.width - PAGE.marginX * 2 - 14) / 2;
  const blocks = [
    { title: 'Situação', items: result.spin.situacao, accent: COLORS.blue700, x: PAGE.marginX + 4 },
    { title: 'Problema', items: result.spin.problema, accent: COLORS.blue500, x: PAGE.marginX + 8 + halfWidth },
    { title: 'Implicação', items: result.spin.implicacao, accent: COLORS.cyan, x: PAGE.marginX + 4 },
    { title: 'Necessidade de solução', items: result.spin.necessidadeSolucao, accent: COLORS.blue700, x: PAGE.marginX + 8 + halfWidth }
  ];

  const uniformHeight = Math.max(
    ...blocks.map((block) => Math.max(42, block.items.reduce((acc, item) => acc + estimateTextHeight(doc, item, halfWidth - 11, 4.1) + 1.8, 0) + 30))
  );
  const secondRowY = y + 18 + uniformHeight;
  const totalHeight = uniformHeight * 2 + 30;

  drawPanel(doc, 'SPIN Selling', 'Diagnóstico consultivo com perguntas abertas e naturais', y, totalHeight);
  blocks.slice(0, 2).forEach((block) => {
    drawSubBlock(doc, block.x, y + 14, halfWidth, block.title, block.items, block.accent, uniformHeight);
  });
  blocks.slice(2).forEach((block) => {
    drawSubBlock(doc, block.x, secondRowY, halfWidth, block.title, block.items, block.accent, uniformHeight);
  });
  return y + totalHeight + 6;
};

const drawSineWavePanel = (doc: jsPDF, result: PreparationResult, y: number): number => {
  const cardWidth = (PAGE.width - PAGE.marginX * 2 - 14) / 2;
  const summaries = result.valueSineMap.steps.map((step) => `${step.resumo} ${step.guiaTatico[0] ?? ''}`.trim());
  const uniformHeight = Math.max(...summaries.map((summary) => Math.max(34, estimateTextHeight(doc, summary, cardWidth - 14, 4.1) + 20)));
  const secondRowY = y + 18 + uniformHeight;
  const thirdRowY = secondRowY + 4 + uniformHeight;
  const totalHeight = uniformHeight * 3 + 26;
  drawPanel(doc, 'Senoide da Venda de Valor', 'Narrativa da conversa em seis etapas', y, totalHeight);
  drawStageCard(doc, PAGE.marginX + 4, y + 14, cardWidth, 1, result.valueSineMap.steps[0].titulo, summaries[0], uniformHeight);
  drawStageCard(doc, PAGE.marginX + 8 + cardWidth, y + 14, cardWidth, 2, result.valueSineMap.steps[1].titulo, summaries[1], uniformHeight);
  drawStageCard(doc, PAGE.marginX + 4, secondRowY, cardWidth, 3, result.valueSineMap.steps[2].titulo, summaries[2], uniformHeight);
  drawStageCard(doc, PAGE.marginX + 8 + cardWidth, secondRowY, cardWidth, 4, result.valueSineMap.steps[3].titulo, summaries[3], uniformHeight);
  drawStageCard(doc, PAGE.marginX + 4, thirdRowY, cardWidth, 5, result.valueSineMap.steps[4].titulo, summaries[4], uniformHeight);
  drawStageCard(doc, PAGE.marginX + 8 + cardWidth, thirdRowY, cardWidth, 6, result.valueSineMap.steps[5].titulo, summaries[5], uniformHeight);
  return y + totalHeight + 6;
};

export const exportPreparationToPdf = async (result: PreparationResult): Promise<void> => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const logoDataUrl = await loadImageAsDataUrl('/cvale-logo.png');
  const footerLines = [
    `Gerado em ${formatGeneratedAt(result.metadata.generatedAt)}`,
    `© 2026 CVale Alimentos | Paixão por Vendas | José Ricardo Noronha®`,
    `Inteligência Comercial aplicada à Venda de Valor | Todos os direitos reservados.`
  ];
  const pageNumberRef = { current: 1 };

  addPageChrome(doc, logoDataUrl, footerLines, pageNumberRef.current);

  let cursorY = PAGE.top + PAGE.headerHeight + 8;
  cursorY = ensureSpace(doc, cursorY, 48, logoDataUrl, footerLines, pageNumberRef);
  cursorY = drawContextPanel(doc, result, cursorY);

  cursorY = ensureSpace(doc, cursorY, 42, logoDataUrl, footerLines, pageNumberRef);
  cursorY = drawScenarioPanel(doc, result, cursorY);

  cursorY = ensureSpace(doc, cursorY, 62, logoDataUrl, footerLines, pageNumberRef);
  cursorY = drawBehaviorPanel(doc, result, cursorY);

  cursorY = ensureSpace(doc, cursorY, 108, logoDataUrl, footerLines, pageNumberRef);
  cursorY = drawTripleBlockPanel(
    doc,
    'OPC',
    'Direção estratégica da reunião comercial',
    [
      { title: 'Objetivo', items: result.opc.objetivo, accent: COLORS.blue700 },
      { title: 'Processo', items: result.opc.processo, accent: COLORS.blue500 },
      { title: 'Compromisso', items: result.opc.compromisso, accent: COLORS.cyan }
    ],
    cursorY,
    { minHeight: 48, extraHeight: 30 }
  );

  cursorY = ensureSpace(doc, cursorY, 108, logoDataUrl, footerLines, pageNumberRef);
  cursorY = drawSpinPanel(doc, result, cursorY);

  cursorY = ensureSpace(doc, cursorY, 92, logoDataUrl, footerLines, pageNumberRef);
  cursorY = drawTripleBlockPanel(
    doc,
    'EPA',
    'Provocação de valor para educar, personalizar e conduzir',
    [
      { title: 'Educar', items: result.epa.educar, accent: COLORS.blue700 },
      { title: 'Personalizar', items: result.epa.personalizar, accent: COLORS.blue500 },
      { title: 'Assumir o controle', items: result.epa.assumirControle, accent: COLORS.cyan }
    ],
    cursorY,
    { minHeight: 46, extraHeight: 28 }
  );

  cursorY = ensureSpace(doc, cursorY, 120, logoDataUrl, footerLines, pageNumberRef);
  cursorY = drawSineWavePanel(doc, result, cursorY);

  doc.save(`${slugify(APP_NAME)}-${slugify(result.metadata.centralGoal)}.pdf`);
};
